// Lib
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DragSourceMonitor, useDrag } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import * as Immutable from 'immutable';

// Singleton
import dragAndDropStateSingleton from '../../dnd/dragAndDropStateSingleton';

// Utils
import * as elementFactory from '../../../../common/elements/elementRegistry';
import * as pointLib from '../../../../common/maths/geometry/point';
import { getScaledGrabOffset } from '../../../utils/dnd/dragAndDropUtils';
import { createSingleEditorTaskListContentWithItems } from '../../../../common/taskLists/singleEditorTaskListContentBuilder';
import translateDropResultIntoElementLocation from '../../dnd/utils/translateDropResultIntoElementLocation';
import {
    clearTaskItemDragSource,
    setTaskItemDragSource,
} from '../../../../common/tiptap/extensions/taskList/taskListItemDragSourcePlugin';

// Actions
import { createElementSync } from '../../actions/elementActions';
import { dragEnd, dragStart } from '../../../reducers/draggingActions';

// Selectors
import { globalGridSizeSelector } from '../../../utils/grid/gridSizeSelector';
import { getCurrentUserId } from '../../../user/currentUserSelector';

// Context
import { useZoomContext } from '../../../canvas/zoom/ZoomStateContext';

// Constants
import { ELEMENT_DND_TYPE } from '../../../../common/elements/elementConstants';
import { DRAG_PREVIEW_ID } from '../../../reducers/draggingConstants';

// Types
import { ElementType } from '../../../../common/elements/elementTypes';
import type { DropResult } from '../../../canvas/dnd/dragTypes';
import type { TaskItemDragHandleComponentProps } from '../../../../common/tiptap/extensions/taskList/TaskListItemDragHandle';

// Components
import TaskDragIcon from '../../../components/icons/light/medium/jsx/task-drag';

import './TaskListDragHandle.scss';

const TASK_LIST_PREVIEW_OFFSET_GU = { x: 28, y: 1 };

/**
 * The drag handle for a single-editor task list item.
 */
const DraggableTaskListDragHandle: React.FC<TaskItemDragHandleComponentProps> = ({ editor, node, getPos }) => {
    const dispatch = useDispatch();
    const gridSize = useSelector(globalGridSizeSelector);
    const currentUserId = useSelector(getCurrentUserId);
    const { getContextZoomScale } = useZoomContext() ?? {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [, connectDragSource, connectDragPreview] = useDrag<any, DropResult, unknown>({
        item: { id: DRAG_PREVIEW_ID, type: ELEMENT_DND_TYPE },
        begin: (monitor: DragSourceMonitor) => {
            dispatch(dragStart({}));

            // Fade the source item for the duration of the drag
            setTaskItemDragSource(editor, getPos(), node);

            const element = Immutable.fromJS(
                elementFactory.createElementObject({
                    id: DRAG_PREVIEW_ID,
                    elementType: ElementType.TASK_LIST_TYPE,
                    content: createSingleEditorTaskListContentWithItems([node.toJSON()]),
                    location: {},
                    meta: {
                        creator: currentUserId,
                    },
                }),
            );

            const zoomScaleOnDragSource = getContextZoomScale?.() ?? 1;

            // "Scaled" offsets are in screen pixels, so the grid offset needs to account for the canvas zoom
            const defaultElementOffset = pointLib.scale(
                -1 * gridSize * zoomScaleOnDragSource,
                TASK_LIST_PREVIEW_OFFSET_GU,
            );

            dragAndDropStateSingleton.scaledGrabOffset = pointLib.scale(-1, defaultElementOffset);

            // @ts-ignore - lodash curry
            const scaledCustomDragOffset = pointLib.translate(
                defaultElementOffset,
                getScaledGrabOffset({
                    clientOffset: monitor.getClientOffset(),
                    sourceClientOffset: monitor.getSourceClientOffset(),
                }),
            );

            return {
                id: DRAG_PREVIEW_ID,
                element,
                isNewElement: true,
                draggedElementIds: [DRAG_PREVIEW_ID],
                draggedElements: [element],
                zoomScaleOnDragSource,
                scaledCustomDragOffset,
                hoveredTypesRegistry: {},
            };
        },
        end: (item, monitor) => {
            // Restore the source item's appearance (drop completed or cancelled via Esc)
            clearTaskItemDragSource(editor);

            const dropResult = monitor.getDropResult();
            const location = translateDropResultIntoElementLocation({
                dropResult,
                unscaledElementOffsetsMap: {},
                elementId: DRAG_PREVIEW_ID,
                gridSize,
                scaledCustomDragOffset: item.scaledCustomDragOffset,
            });

            if (location) {
                dispatch(
                    createElementSync({
                        elementType: ElementType.TASK_LIST_TYPE,
                        location,
                        content: createSingleEditorTaskListContentWithItems([node.toJSON()]),
                    }),
                );
            }

            dispatch(dragEnd({}));
        },
    });

    /**
     * Drag preview
     */
    useEffect(() => {
        if (!connectDragPreview) return;
        connectDragPreview(getEmptyImage());
    }, [connectDragPreview]);

    return connectDragSource(
        <div className="TaskListDragHandle">
            <TaskDragIcon />
        </div>,
    );
};

/**
 * Renders the draggable handle, or nothing at all when the board can't be modified
 * (read-only boards / permissions).
 */
const TaskListDragHandle: React.FC<TaskItemDragHandleComponentProps> = (props) =>
    props.canDrag ? <DraggableTaskListDragHandle {...props} /> : null;

export default TaskListDragHandle;
